import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import {
  Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Chip,
  TextField, Checkbox,
  Tooltip, alpha, useTheme, Skeleton, InputAdornment, Avatar,
  Autocomplete, Collapse, Divider,
} from '@mui/material';
import {
  Check, Close, Block, CheckCircle, Cancel, Receipt,
  FilterList, ExpandMore, ExpandLess, Clear, Undo,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import expenseService, { ExportExpenseParams, ApprovalSubmitter } from '../../services/expenseService';
import expenseCategoryService from '../../services/expenseCategoryService';
import { Expense, ExpenseStatus, ExpenseCategory, ExpenseSubCategory } from '../../types';
import ViewExpenseDialog from '../../components/expenses/ViewExpenseDialog';
import ExpenseStatusChip from '../../components/expenses/ExpenseStatusChip';
import { EXPENSE_STATUS_FILTER_OPTIONS, expenseStatusColor } from '../../components/expenses/expenseStatusFilter';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import DateRangePicker from '../../components/common/DateRangePicker';
import CopyableIdCell from '../../components/common/CopyableIdCell';
import TagChip from '../../components/common/TagChip';
import SearchableSelect, { SelectOption } from '../../components/common/SearchableSelect';
import { tagColor } from '../../utils/tagColor';
import { useAuth } from '../../context/AuthContext';
import DownloadMenu from '../../components/common/DownloadMenu';
import ExpenseReportDocument from '../../components/finance/pdf/ExpenseReportDocument';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import { useApprovalRule } from '../../hooks/useApprovalRule';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import ReasonDialog from '../../components/common/ReasonDialog';

const ExpenseApprovalPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const money = useCurrencyFormat();
  const { currency, decodedToken } = useAuth();
  // With a second person required, nobody approves an expense they submitted.
  const { ownBlocked } = useApprovalRule();
  const mustLeave = (e: Expense) => ownBlocked(e.createdBy?._id === decodedToken?.userId);
  const { canRead, canWrite } = useModulePermissions();

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  const [subCategories, setSubCategories] = useState<ExpenseSubCategory[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userFilter, setUserFilter] = useState<ApprovalSubmitter | null>(null);
  const [batchIdFilter, setBatchIdFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [users, setUsers] = useState<ApprovalSubmitter[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchInput, setUserSearchInput] = useState('');
  const debouncedBatchId = useDebounce(batchIdFilter, 500);
  const debouncedUserSearch = useDebounce(userSearchInput, 400);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [viewOpen, setViewOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Single-row reversal — triggered from the per-row action on APPROVED rows.
  const [reverseOneOpen, setReverseOneOpen] = useState(false);

  // Batch reversal — only enabled while the table is filtered to one batchId.
  const [reverseBatchOpen, setReverseBatchOpen] = useState(false);

  // Selection reversal — same reason-dialog pattern, scoped to the IDs the
  // user has checkboxed. Enabled when every selected row is a reversible
  // APPROVED row (not pending, not already reversed).
  const [reverseSelectedOpen, setReverseSelectedOpen] = useState(false);


  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      if (subCategoryFilter) params.subCategoryId = subCategoryFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (userFilter) params.createdBy = userFilter._id;
      if (debouncedBatchId) params.batchId = debouncedBatchId;

      const data = await expenseService.getPendingExpenses(params);
      setExpenses(data.expenses);
      setTotalItems(data.pagination.totalItems);
      setPendingTotal(data.pendingTotal);
      setSelected(new Set());
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load expenses', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [page, rowsPerPage, statusFilter, categoryFilter, subCategoryFilter, dateFrom, dateTo, userFilter, debouncedBatchId, enqueueSnackbar]);

  useEffect(() => { loadExpenses(); }, [loadExpenses]);
  useEffect(() => {
    expenseCategoryService.getCategories().then(setCategories).catch(() => {});
  }, []);
  useEffect(() => { loadUsers(debouncedUserSearch); }, [debouncedUserSearch]);

  const loadUsers = async (search: string) => {
    setUsersLoading(true);
    try {
      const data = await expenseService.getApprovalContext({ search, limit: 50 });
      setUsers(data?.submitters ?? []);
    } catch {
      // non-critical
    } finally {
      setUsersLoading(false);
    }
  };

  const advancedFilterCount = [dateFrom, dateTo, userFilter, batchIdFilter].filter(Boolean).length;
  const hasAnyFilter = statusFilter || categoryFilter || subCategoryFilter || dateFrom || dateTo || userFilter || batchIdFilter;

  const handleClearAllFilters = () => {
    setStatusFilter('');
    setCategoryFilter('');
    setSubCategoryFilter('');
    setSubCategories([]);
    setDateFrom('');
    setDateTo('');
    setUserFilter(null);
    setBatchIdFilter('');
    setPage(0);
  };

  const handleCategoryFilterChange = async (categoryId: string) => {
    setCategoryFilter(categoryId);
    setSubCategoryFilter('');
    setPage(0);
    if (categoryId) {
      try {
        const subs = await expenseCategoryService.getSubCategories(categoryId);
        setSubCategories(subs);
      } catch {
        setSubCategories([]);
      }
    } else {
      setSubCategories([]);
    }
  };

  // Row is "reversible" when it's an APPROVED row that hasn't been reversed —
  // used both to enable per-row checkboxes for approved rows (so users can
  // pick a subset to reverse) and to decide what the batch-scope button does
  // when a selection exists.
  const isReversibleRow = (e: Expense) =>
    e.status === ExpenseStatus.APPROVED && e.isActive !== false;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select-all keeps its original scope: PENDING rows only. Approved rows
      // must be picked individually to avoid an accidental mass-reverse.
      setSelected(new Set(expenses.filter(e => e.status === ExpenseStatus.PENDING).map(e => e._id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const pendingOnPage = expenses.filter(e => e.status === ExpenseStatus.PENDING);
  const allPendingSelected = pendingOnPage.length > 0 && pendingOnPage.every(e => selected.has(e._id));

  // Per-action subset counts of the current selection. Approve/Reject only
  // affect pending rows; Reverse only affects reversible-approved rows. The
  // bulk UI shows each action's real impact, not the raw selection size, so
  // ticking an approved row doesn't make "Approve Selected" claim it will
  // approve that row.
  const selectedExpenseList = expenses.filter(e => selected.has(e._id));
  const selectedPendingCount = selectedExpenseList.filter(e => e.status === ExpenseStatus.PENDING).length;
  const selectedApprovable = selectedExpenseList.filter(e => e.status === ExpenseStatus.PENDING && !mustLeave(e));
  const selectedReversibleCount = selectedExpenseList.filter(isReversibleRow).length;

  const handleApprove = async () => {
    if (!selectedExpense) return;
    try {
      await expenseService.approveExpense(selectedExpense._id);
      enqueueSnackbar('Expense approved', { variant: 'success' });
      setApproveOpen(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to approve', { variant: 'error' });
    }
  };

  // ReasonDialog shows a failure inline and keeps the dialog open.
  const handleReject = async (reason: string) => {
    if (!selectedExpense) return;
    await expenseService.rejectExpense(selectedExpense._id, reason);
    enqueueSnackbar('Expense rejected', { variant: 'success' });
    setSelectedExpense(null);
    loadExpenses();
  };

  const handleBulkApprove = async () => {
    try {
      // Only send pending IDs — any non-pending row in the selection would
      // be rejected by the backend and inflate the "failed" count.
      const ids = selectedApprovable.map(e => e._id);
      const result = await expenseService.bulkApprove(ids);
      enqueueSnackbar(`${result.approved} expense(s) approved`, { variant: 'success' });
      setBulkApproveOpen(false);
      setSelected(new Set());
      loadExpenses();
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to bulk approve', { variant: 'error' });
    }
  };

  const handleBulkReject = async (reason: string) => {
    // Mirror of handleBulkApprove — only pending rows can be rejected.
    const ids = selectedExpenseList
      .filter(e => e.status === ExpenseStatus.PENDING)
      .map(e => e._id);
    const result = await expenseService.bulkReject(ids, reason);
    enqueueSnackbar(`${result.rejected} expense(s) rejected`, { variant: 'success' });
    setSelected(new Set());
    loadExpenses();
  };

  // Reversals run through ReasonDialog: it collects the reason, shows
  // errors in place, and closes on success.
  const handleReverseOne = async (reason: string) => {
    if (!selectedExpense) return;
    await expenseService.reverseExpense(selectedExpense._id, reason);
    enqueueSnackbar('Expense reversed', { variant: 'success' });
    setSelectedExpense(null);
    // Deliberately do NOT clear `selected` — single-row reversal has nothing
    // to do with the user's bulk-approve selection on other rows.
    loadExpenses();
  };

  const reportBulkReversal = (result: { reversed: number; failed?: unknown[] }) => {
    const skipped = result.failed?.length ?? 0;
    enqueueSnackbar(
      `${result.reversed} expense${result.reversed === 1 ? '' : 's'} reversed${skipped ? `, ${skipped} skipped` : ''}`,
      { variant: skipped ? 'warning' : 'success' },
    );
    setSelected(new Set());
    loadExpenses();
  };

  const handleReverseBatch = async (reason: string) => {
    if (!debouncedBatchId) return;
    reportBulkReversal(await expenseService.reverseBatch(debouncedBatchId, reason));
  };

  const handleReverseSelected = async (reason: string) => {
    // Send only the reversible IDs, not the raw selection — the button
    // label advertises "Reverse selected (N)" using the reversible count,
    // so the request body has to match that promise.
    const ids = selectedExpenseList.filter(isReversibleRow).map(e => e._id);
    if (ids.length === 0) return;
    reportBulkReversal(await expenseService.reverseSelected(ids, reason));
  };

  const getExportParams = useCallback((): ExportExpenseParams => {
    const params: ExportExpenseParams = {};
    if (statusFilter) params.status = statusFilter;
    if (categoryFilter) {
      params.categoryId = categoryFilter;
      const cat = categories.find(c => c._id === categoryFilter);
      if (cat) params.categoryName = cat.name;
    }
    if (subCategoryFilter) {
      params.subCategoryId = subCategoryFilter;
      const sub = subCategories.find(s => s._id === subCategoryFilter);
      if (sub) params.subCategoryName = sub.name;
    }
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (userFilter) {
      params.createdBy = userFilter._id;
      params.submittedByName = userFilter.name;
    }
    if (debouncedBatchId) params.batchId = debouncedBatchId;
    return params;
  }, [statusFilter, categoryFilter, subCategoryFilter, dateFrom, dateTo, userFilter, debouncedBatchId, categories, subCategories]);

  // The PDF is drawn in the browser from the filtered report; the CSV comes from the server.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const handleExport = async (type: 'pdf' | 'csv') => {
    const params = getExportParams();
    if (type === 'csv') return expenseService.exportExpensesCSV(params);
    await generatePdf(async () => (
      <ExpenseReportDocument
        report={await expenseService.getExpenseReport(params)}
        currency={currency || 'PKR'}
        branding={branding}
        generatedAt={new Date().toISOString()}
      />
    ), `expense-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  // "Reversed" is a derived state: status still reads APPROVED on the row
  // but isActive is false. In this module the only path to that shape is a
  // reversal (deleteExpense refuses non-PENDING), so isActive alone is
  // sufficient — older reversals from before the audit fields existed
  // still get flagged correctly. Kept local because several downstream
  // bits (batch-reverse guard, action-cell gating) need the predicate.
  const isReversed = (e: Expense) =>
    e.status === ExpenseStatus.APPROVED && e.isActive === false;

  // ─── Filter option builders ─────────────────────────────────────────────
  const categoryOptions: SelectOption[] = [
    { value: '', label: 'All' },
    ...categories.map(c => ({ value: c._id, label: c.name })),
  ];
  const subCategoryOptions: SelectOption[] = [
    { value: '', label: 'All' },
    ...subCategories.map(sc => ({ value: sc._id, label: sc.name })),
  ];
  // Tiny dot for the dropdown-row adornment — a visual anchor so each
  // option carries the same colour language as the status chip / category
  // chip on the row below.
  const ColorDot: React.FC<{ color: string }> = ({ color }) => (
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
  );

  if (!canRead('expense_approval')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight="600" gutterBottom>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to view Expense Approvals.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Expense Approvals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve employee expense claims
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {pendingTotal > 0 && (
            <Chip
              label={`${pendingTotal} pending`}
              color="warning"
            />
          )}
          <DownloadMenu onExport={handleExport} />
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <SearchableSelect
                label="Status"
                value={statusFilter}
                onChange={(v) => { setStatusFilter(v); setPage(0); }}
                options={EXPENSE_STATUS_FILTER_OPTIONS}
                placeholder="All"
                fullWidth
                minWidth={0}
                renderOptionAdornment={(opt) => opt.value ? <ColorDot color={expenseStatusColor(theme, opt.value)} /> : null}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
              <SearchableSelect
                label="Category"
                value={categoryFilter}
                onChange={(v) => handleCategoryFilterChange(v)}
                options={categoryOptions}
                placeholder="All categories"
                fullWidth
                minWidth={0}
                renderOptionAdornment={(opt) => opt.value ? <ColorDot color={tagColor(opt.label)} /> : null}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2.5 }}>
              <SearchableSelect
                label="Sub-Category"
                value={subCategoryFilter}
                onChange={(v) => { setSubCategoryFilter(v); setPage(0); }}
                options={subCategoryOptions}
                placeholder={categoryFilter ? 'All sub-categories' : 'Select a category first'}
                disabled={!categoryFilter || subCategories.length === 0}
                fullWidth
                minWidth={0}
                renderOptionAdornment={(opt) => opt.value ? <ColorDot color={tagColor(opt.label)} /> : null}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 'auto' }} sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FilterList fontSize="small" />}
                endIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{
                  textTransform: 'none',
                  borderColor: advancedFilterCount > 0 ? theme.palette.primary.main : theme.palette.divider,
                  color: advancedFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
                  bgcolor: showAdvancedFilters ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  },
                }}
              >
                More Filters{advancedFilterCount > 0 ? ` (${advancedFilterCount})` : ''}
              </Button>
              {hasAnyFilter && (
                <Tooltip title="Reset all filters">
                  <IconButton size="small" onClick={handleClearAllFilters} sx={{ color: 'text.secondary' }}>
                    <Clear fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        </Box>

        <Collapse in={showAdvancedFilters}>
          <Divider />
          <Box
            sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 12, md: 5 }}>
                <DateRangePicker
                  fullWidth
                  label="Date range"
                  value={{ from: dateFrom || null, to: dateTo || null }}
                  onChange={(r) => {
                    setDateFrom(r.from || '');
                    setDateTo(r.to || '');
                    setPage(0);
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Autocomplete
                  size="small"
                  options={users}
                  value={userFilter}
                  loading={usersLoading}
                  getOptionLabel={(option) => option.name || option.email || ''}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  onInputChange={(_, value) => setUserSearchInput(value)}
                  onChange={(_, value) => { setUserFilter(value); setPage(0); }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option._id}>
                      <Avatar sx={{ width: 28, height: 28, mr: 1.5, bgcolor: theme.palette.primary.main, fontSize: '0.75rem' }}>
                        {option.name?.charAt(0) || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Submitted By"
                      placeholder="Search employees..."
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Batch ID"
                  placeholder="e.g. BATCH-001"
                  value={batchIdFilter}
                  onChange={(e) => { setBatchIdFilter(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Receipt fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Batch Scope Actions — visible while the table is filtered to one batchId */}
      {debouncedBatchId && (() => {
        // The reverse button has two modes depending on whether the
        // selection has any reversible rows:
        //   - Selection contains ≥1 reversible row → "Reverse selected (N)"
        //     where N counts ONLY the reversible rows (not raw selection
        //     size). Clicking posts just those IDs; pending/reversed rows
        //     in the selection are not sent.
        //   - Otherwise (no selection, or selection has only non-reversible
        //     rows like pending/reversed) → "Reverse entire batch" acts on
        //     the whole batchId.
        //
        // The approve/reject bulk bar below is independent and counts the
        // pending subset of the selection.
        const hasReversibleInSelection = selectedReversibleCount > 0;
        // Only live approved rows are reversible; this gates the whole-batch
        // button when no selection drives the action.
        const hasApprovedVisible = expenses.some(
          (e) => e.status === ExpenseStatus.APPROVED && e.isActive !== false,
        );
        const wholeBatchDisabled = !hasReversibleInSelection && expenses.length > 0 && !hasApprovedVisible;
        return (
          <Paper
            elevation={0}
            sx={{
              p: 1.5, mb: 2,
              display: 'flex', alignItems: 'center', gap: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              bgcolor: alpha(theme.palette.warning.main, 0.04),
            }}
          >
            <Receipt fontSize="small" sx={{ color: theme.palette.warning.dark }} />
            <Typography variant="body2" component="div" sx={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              Viewing batch <CopyableIdCell value={debouncedBatchId} fontWeight={400} truncate />
            </Typography>
            {canWrite('expense_approval') && (
              <Tooltip title={wholeBatchDisabled ? 'No approved expenses on this page to reverse' : ''}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Undo fontSize="small" />}
                    onClick={() => hasReversibleInSelection ? setReverseSelectedOpen(true) : setReverseBatchOpen(true)}
                    disabled={wholeBatchDisabled}
                  >
                    {hasReversibleInSelection ? `Reverse selected (${selectedReversibleCount})` : 'Reverse entire batch'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Paper>
        );
      })()}

      {/* Bulk Actions — Approve/Reject only count the pending rows in the
          selection, because that's the subset they can actually act on.
          A selection with no pending rows (e.g. only approved rows picked
          for a reverse flow) hides these buttons entirely. */}
      {selected.size > 0 && canWrite('expense_approval') && selectedPendingCount > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">{selected.size} selected</Typography>
          <Tooltip title={selectedApprovable.length < selectedPendingCount ? 'Expenses you submitted are left for someone else to approve.' : ''}>
            <span>
              <Button variant="contained" color="success" size="small" startIcon={<Check />} disabled={selectedApprovable.length === 0} onClick={() => setBulkApproveOpen(true)}>
                Approve Selected ({selectedApprovable.length})
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" color="error" size="small" startIcon={<Close />} onClick={() => setBulkRejectOpen(true)}>
            Reject Selected ({selectedPendingCount})
          </Button>
        </Paper>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!initialLoad && loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {canWrite('expense_approval') && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allPendingSelected}
                      indeterminate={selected.size > 0 && !allPendingSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={pendingOnPage.length === 0}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sub-Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Batch ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Submitted By</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canWrite('expense_approval') ? 12 : 11} sx={{ textAlign: 'center', py: 6 }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {statusFilter || categoryFilter || subCategoryFilter || dateFrom || dateTo || userFilter || batchIdFilter
                        ? 'No expenses match your filters'
                        : 'No expense submissions found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => {
                  const isPending = expense.status === ExpenseStatus.PENDING;
                  const reversed = isReversed(expense);
                  return (
                    <TableRow
                      key={expense._id}
                      hover
                      sx={{ cursor: 'pointer', opacity: reversed ? 0.6 : 1 }}
                      onClick={() => { setSelectedExpense(expense); setViewOpen(true); }}
                    >
                      {canWrite('expense_approval') && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selected.has(expense._id)}
                            onChange={() => handleSelectOne(expense._id)}
                            disabled={!isPending && !isReversibleRow(expense)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {expense.title}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{currency}</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {expense.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(expense.date), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TagChip value={expense.categoryId?.name} />
                      </TableCell>
                      <TableCell>
                        <TagChip value={expense.subCategoryId?.name} />
                      </TableCell>
                      <TableCell>
                        <CopyableIdCell value={expense.batchId} fontWeight={400} truncate />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {expense.createdBy.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ExpenseStatusChip expense={expense} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(expense.createdAt), 'MMM dd, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {isPending && canWrite('expense_approval') ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={mustLeave(expense) ? 'You submitted this — someone else must approve it.' : 'Approve'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  disabled={mustLeave(expense)}
                                  onClick={() => { setSelectedExpense(expense); setApproveOpen(true); }}
                                >
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => { setSelectedExpense(expense); setRejectOpen(true); }}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <>
                            {expense.status === ExpenseStatus.APPROVED && !reversed && (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="success.main">
                                  Approved
                                </Typography>
                                {canWrite('expense_approval') && (
                                  <Tooltip title="Reverse — posts a compensating ledger entry">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => { setSelectedExpense(expense); setReverseOneOpen(true); }}
                                    >
                                      <Undo fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                            {reversed && (
                              <Typography variant="caption" color="text.secondary">
                                —
                              </Typography>
                            )}
                            {expense.status === ExpenseStatus.REJECTED && (
                              <Tooltip title={expense.rejectionReason || 'No reason provided'}>
                                <Typography variant="caption" color="error.main" sx={{ cursor: 'help' }}>
                                  Rejected
                                </Typography>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* View Dialog */}
      <ViewExpenseDialog open={viewOpen} expense={selectedExpense}
        onClose={() => { setViewOpen(false); setSelectedExpense(null); }} />

      {/* Approve Confirm */}
      <ConfirmationDialog
        open={approveOpen}
        title="Approve Expense"
        message={`Approve "${selectedExpense?.title}" for ${money(selectedExpense?.amount ?? 0)}?`}
        confirmText="Yes, Approve"
        cancelText="Cancel"
        confirmColor="success"
        onConfirm={handleApprove}
        onClose={() => { setApproveOpen(false); setSelectedExpense(null); }}
      />

      {/* Reject Dialog */}
      <ReasonDialog
        open={rejectOpen}
        onClose={() => { setRejectOpen(false); setSelectedExpense(null); }}
        title="Reject this expense?"
        facts={selectedExpense ? [
          { label: 'Expense', value: selectedExpense.title },
          { label: 'Amount', value: money(selectedExpense.amount) },
        ] : []}
        consequence="Nothing is posted. The person who submitted it sees your reason."
        confirmText="Reject"
        reasonPlaceholder="Why is this expense rejected?"
        onConfirm={handleReject}
      />

      {/* Bulk Approve Confirm */}
      <ConfirmationDialog
        open={bulkApproveOpen}
        title="Bulk Approve"
        message={`Approve ${selectedApprovable.length} selected pending expense${selectedApprovable.length === 1 ? "" : "s"}?`}
        confirmText="Approve All"
        confirmColor="success"
        onConfirm={handleBulkApprove}
        onClose={() => setBulkApproveOpen(false)}
      />

      <ReasonDialog
        open={bulkRejectOpen}
        onClose={() => setBulkRejectOpen(false)}
        title={`Reject ${selectedPendingCount} expense${selectedPendingCount === 1 ? '' : 's'}?`}
        consequence="Nothing is posted. The people who submitted them see your reason."
        confirmText="Reject all"
        reasonPlaceholder="Why are these expenses rejected?"
        onConfirm={handleBulkReject}
      />

      <ReasonDialog
        open={reverseOneOpen}
        onClose={() => { setReverseOneOpen(false); setSelectedExpense(null); }}
        title="Reverse this expense?"
        facts={selectedExpense ? [
          { label: 'Expense', value: selectedExpense.title },
          { label: 'Amount', value: money(selectedExpense.amount) },
        ] : []}
        consequence="The expense no longer counts and the reversal is recorded in the books today. The expense stays on record, marked reversed."
        confirmText="Reverse expense"
        onConfirm={handleReverseOne}
      />
      <ReasonDialog
        open={reverseBatchOpen}
        onClose={() => setReverseBatchOpen(false)}
        title="Reverse this batch?"
        facts={[{ label: 'Batch', value: debouncedBatchId }]}
        consequence="Every approved expense in this batch is reversed and each reversal is recorded in the books today. The expenses stay on record, marked reversed — re-upload corrected rows afterwards if needed."
        confirmText="Reverse batch"
        onConfirm={handleReverseBatch}
      />
      <ReasonDialog
        open={reverseSelectedOpen}
        onClose={() => setReverseSelectedOpen(false)}
        title={`Reverse ${selectedReversibleCount} expense${selectedReversibleCount === 1 ? '' : 's'}?`}
        facts={[
          { label: 'Expenses', value: selectedReversibleCount },
          { label: 'Total', value: money(selectedExpenseList.filter(isReversibleRow).reduce((sum, e) => sum + (e.amount || 0), 0)) },
        ]}
        consequence="Each selected expense is reversed and the reversals are recorded in the books today. The expenses stay on record, marked reversed."
        confirmText={`Reverse ${selectedReversibleCount}`}
        onConfirm={handleReverseSelected}
      />
    </Box>
  );
};

export default ExpenseApprovalPage;
